import { Component, HostListener, Inject, Input, OnInit, } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from "@environment";
import {
  DEFAULT_PAGE_SIZE,
  FindUsersDetailsRequest, Paging,
  UserDetail,
  UsersDetailFilter
} from '@model';
import { DOCUMENT } from '@angular/common';
import { DETAILS } from 'src/app/app-routing.module';
import { AbstractComponent } from "@components";
import { AdministratorsService, ExportService } from "@services";
import { debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-list-of-users',
  templateUrl: './list-of-users.component.html',
  styleUrls: ['./list-of-users.component.scss'],
})

export class ListOfUsersComponent extends AbstractComponent implements OnInit, Paging<UserDetail> {

  @Input() users: UserDetail[] = [];
  searchText: string = "";
  page: number = 0;
  pageExport: number = 0;
  hasMoreUsers: boolean = true;
  environment: any = environment;
  showFabButton: boolean = false;
  selectedRole: number | undefined;
  roles: number[] = UsersDetailFilter.ROLES;
  isLoading: boolean = false;
  queryParamsFirstChange: boolean = true;
  filter: FindUsersDetailsRequest | undefined;

  constructor(
    private route: ActivatedRoute,
    private adminService: AdministratorsService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    private exportService: ExportService,
  ) {
    super();
  }

  @HostListener('window:scroll')
  toggleFabButton() {
    this.showFabButton = window.pageYOffset != 0;
  }

  ngOnInit(): void {
    this.handleExploreResult(this.users);
    this.subsribeToRouteDataChange();
  }

  subsribeToRouteDataChange() {
    this.subscriptions.push(this.route.queryParams.pipe(debounceTime(environment.fullTextSearchDelay),
      distinctUntilChanged()).subscribe((queryParams: any) => {
        this.searchText = queryParams.search ? queryParams.search : "";
        this.selectedRole = this.getRoleValue(queryParams)
        if (!this.queryParamsFirstChange && this.isRoleValid(queryParams.role)) {
          this.onExplore(false);
        }
        this.queryParamsFirstChange = false;
      })
    );
  }

  getFileFromServer(type: string) {
  if(this.filter===undefined || this.filter === null || !this.filter){
   this.filter =  this.createFilterExport();
  }

    this.exportService.downloadUsersByRole(type, this.filter).subscribe(
      (blob: Blob) => {
        const formattedDateTime: string = this.getCurrentDateTimeFormatted();
        const filename: string = type + '_' + formattedDateTime + '.' + type;
        const downloadLink: HTMLAnchorElement = this.document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(blob);
        downloadLink.setAttribute('download', filename);
        this.document.body.appendChild(downloadLink);
        downloadLink.click();
      },
      error => {
        console.error('Error downloading file:', error);
      }
    );
  }

  getCurrentDateTimeFormatted(): string {
    const currentDateTime = new Date();
    const year: number = currentDateTime.getFullYear();
    const month: string = this.padNumber(currentDateTime.getMonth() + 1, 2);
    const day: string = this.padNumber(currentDateTime.getDate(), 2);
    return `${year}-${month}-${day}`;
  }

  padNumber(num: number, size: number): string {
    let numStr: string = num.toString();
    while (numStr.length < size) {
      numStr = '0' + numStr;
    }
    return numStr;
  }

  private getRoleValue(queryParams: any) {
    if (queryParams.role) {
      return this.isRoleValid(queryParams.role) ? +queryParams.role : undefined
    }
    return undefined;
  }
  private isRoleValid(roleId: string | undefined) {
    if (!roleId) return true;
    else {
      return this.roles.includes(+roleId)
    }
  }

  private changeQueryParams() {
    let queryParams: { [key: string]: any } = {};
    if (this.selectedRole) {
      queryParams['role'] = this.selectedRole;
    }
    if (this.searchText) {
      queryParams['search'] = this.searchText;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true,
    });
  }

  private createFilterExport() {
    return FindUsersDetailsRequest.createUsersDetailsRequest(
      this.pageExport,
      {
        text: this.searchText !== "" ? this.searchText : undefined,
        role: this.selectedRole ?? undefined
      });
  }
  private createFilter() {
    return FindUsersDetailsRequest.createUsersDetailsRequest(
      this.page,
      {
        text: this.searchText !== "" ? this.searchText : undefined,
        role: this.selectedRole ?? undefined
      });
  }

  handleExploreResult(users: UserDetail[], loadMore?: boolean) {
    if (users.length > DEFAULT_PAGE_SIZE) {
      this.hasMoreUsers = true;
      users = users.slice(0, DEFAULT_PAGE_SIZE);
    } else {
      this.hasMoreUsers = false;
    }
    if (loadMore && this.users) {
      users = this.users.concat(users);
    }
    this.setListOfItems(users);
  }


  setListOfItems(value: UserDetail[] | null) {
    if (value && value.length > 0) {
      this.users = value;
      this.page++;
      this.pageExport++;
    } else {
      this.users = [];
    }
  }

  onExplore(loadMore?: boolean) {
    if (loadMore !== true) {
      this.page = 1;
      this.hasMoreUsers = true;
    }
    if (this.hasMoreUsers) {
      this.pageExport++;
      let filter: FindUsersDetailsRequest = this.createFilter();
      this.filter = this.createFilterExport();
      this.adminService.findUsers(filter, !loadMore, () => {
        this.isLoading = loadMore ? this.isLoading : true;
      }, () => {
        setTimeout(() => {
          this.isLoading = loadMore ? this.isLoading : false;
        }, 700);
      }).subscribe((value: UserDetail[]) => {
        this.handleExploreResult(value, loadMore);
      }
      );
    }
  }

  loadMore() :void {
    this.onExplore(true);
  }

  trackByFn(index: number, item: UserDetail) {
    return item.id;
  }

  handleInput(value: string) {
    this.searchText = value.trim() ?? "";
    this.changeQueryParams()
  }

  onRoleSelect(role?: number) {
    if (this.selectedRole === role || !role) {
      this.selectedRole = undefined;
    } else {
      this.selectedRole = role;
    }
    this.changeQueryParams();
  }

  scrollToTop(): void {
    return this.document.body.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'start'
    });
  }

  openUserDetails(user: UserDetail): void {
    this.adminService.currentUserDetails.next(user);
    this.router.navigate([DETAILS], { queryParams: { userId: user.userId }, relativeTo:this.route })
  }
}


